// Browser error reporting for the wasm editor. See PLAN-wasm-error-reporting.md in the engine repo.
//
// This file is the ONLY place Sentry is named. @polygonjs/js-core deliberately has no reporting
// dependency: the engine calls an unregistered sink until a host registers one, so the canvas web
// export and SDK projects report nowhere, and polygonjs.com opts in here.
//
// What reaches Sentry, and how:
//   - JS-layer errors and unhandled rejections — captured by the SDK's own global handlers.
//   - Hard wasm traps — they surface as a thrown RuntimeError, so likewise automatic.
//   - Everything else the ENGINE knows about — a failed Jai assert, WebGPU device loss, an
//     uncaptured GPU error, a dead worker, the renderer giving up — reaches us ONLY through the sink
//     registered below. None of those produce an exception on their own.
//
// What must never reach Sentry: scene content (customer IP), user identity, or anything from the
// page URL. Scenes are the reason the native crash pipeline stores its bundles on our own
// infrastructure instead; the same posture applies here.

import * as Sentry from "@sentry/browser";
import { wasmIntegration } from "@sentry/wasm";
import type { PolygonCoreModule } from "./polygonRuntime";

type ErrorSink = (error: Error, context?: Record<string, string | number | boolean>) => void;

// Sentry is initialised before the engine's JavaScript is even fetched: a boot failure is exactly
// what must be reported, so the sink cannot be registered at the same moment. editorInit calls
// `registerEngineErrorSink` with the js-core it resolved for this scene's version.
let _sink: ErrorSink | null = null;

/**
 * Report a failure raised on the HOST side of the boundary, before or outside the engine: the one
 * that matters most is the per-version bundle fetch failing, which is a page that cannot boot at
 * all and would otherwise end in a console.error nobody sees.
 */
export function reportHostError(error: unknown, context?: Record<string, string | number | boolean>): void {
	if (!_sink) return;
	_sink(error instanceof Error ? error : new Error(String(error)), context);
}

export interface InitErrorReportingOptions {
	// The engine version being served, from the editor mount point. Doubles as the securewasm URL
	// segment and as the native crash bundle's identifier, so web and desktop reports key on one thing.
	release: string;
}

// Read once at module load: the layout only emits these on editor pages, and only when a DSN is set.
function metaContent(name: string): string | undefined {
	return document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content || undefined;
}

let _initialized = false;

/**
 * Initialise reporting and hand the engine its sink. Safe to call when no DSN is configured (the
 * common case in development) — it simply does nothing, leaving the engine's sink unregistered.
 *
 * Call this BEFORE the wasm is fetched, or boot failures go unreported.
 */
export function initErrorReporting(options: InitErrorReportingOptions): void {
	if (_initialized) return;
	const dsn = metaContent("sentry-dsn");
	if (!dsn) return;
	_initialized = true;

	Sentry.init({
		dsn,
		environment: metaContent("sentry-environment") ?? "unknown",
		release: options.release || "unknown",
		// The wasm integration resolves memory addresses in stack frames against the loaded modules.
		// We upload no debug files to Sentry — the shipped bundle carries its own name section, so
		// frames are already legible, and the DWARF stays in our private release archive.
		integrations: [wasmIntegration()],
		// Errors only. No performance/tracing data is wanted, and none is worth the request volume.
		tracesSampleRate: 0,
		// The Sentry wasm guide's sample sets this TRUE. Explicitly off: it would attach IP addresses
		// and request headers, and we report anonymously.
		sendDefaultPii: false,
		beforeSend(event) {
			return scrubEvent(event);
		},
	});

	// `context` is the engine's structured detail (GPU adapter, which wasm bundle loaded, occurrence
	// counts): attached as `extra` rather than tags, since tags are indexed and these are
	// high-cardinality. Held until a js-core is resolved, which is what actually has somewhere to put it.
	_sink = (error, context) => {
		Sentry.captureException(error, context ? { extra: { ...context } } : undefined);
	};
}

/**
 * Hand the engine's failure sink to the js-core this page resolved. Called by editorInit once the
 * version's bundle has loaded; a no-op when no DSN is configured, which is the common case locally.
 *
 * `setErrorSink` is looked up DEFENSIVELY rather than called by name. The site serves several engine
 * versions at once now (PLAN-per-version-js-core.md), and an older one simply does not export it;
 * against such a version we register nothing and Sentry still captures everything the browser raises
 * by itself, which is a working degradation rather than a boot failure.
 */
export function registerEngineErrorSink(core: PolygonCoreModule): void {
	if (!_sink) return;
	const setErrorSink = (core as unknown as {setErrorSink?: (sink: ErrorSink | null) => void}).setErrorSink;
	if (!setErrorSink) {
		// Engine-side failures (asserts, device loss) go unreported on this version; browser-raised
		// errors are unaffected.
		console.warn("polygon: this js-core has no error sink: engine-side failures will not be reported");
		return;
	}
	setErrorSink(_sink);
}

// Strip anything that could carry scene content or identify the user. Belt-and-braces: the engine
// never puts scene data in a report, but this is the last gate before the event leaves the browser,
// and it is the one place a future careless `extra` would be caught.
function scrubEvent<T extends Sentry.ErrorEvent>(event: T): T {
	// A scene URL is /:username/:scenename — the path alone identifies a person and their work.
	if (event.request?.url) {
		event.request.url = stripUrl(event.request.url);
	}
	if (event.request?.headers) {
		delete event.request.headers;
	}
	delete event.user;
	if (event.breadcrumbs) {
		event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
			if (typeof breadcrumb.data?.url === "string") {
				return { ...breadcrumb, data: { ...breadcrumb.data, url: stripUrl(breadcrumb.data.url) } };
			}
			return breadcrumb;
		});
	}
	return event;
}

// Keep the origin, drop the path, query and fragment: which deployment it was is useful, whose scene
// it was is not ours to collect.
function stripUrl(url: string): string {
	try {
		return new URL(url, window.location.origin).origin;
	} catch {
		return "";
	}
}
